import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";

/**
 * Hook that reads CMS page data from the DB (via SiteConfig)
 * and falls back to i18n translations if no DB override exists.
 *
 * Usage:
 *   const ct = useCmsText("about");  // reads page_about_json
 *   ct("title")        → DB value for "title" in current locale, or t("about.title")
 *   ct("whoWeAreText") → DB value or t("about.whoWeAreText")
 */
export function useCmsText(page: "about" | "fiscalita" | "contact") {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const lang = locale || "it";

  const cmsData = useMemo(() => {
    const raw =
      page === "about" ? cfg.page_about_json :
      page === "fiscalita" ? cfg.page_fiscal_json :
      cfg.page_contact_json;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Record<string, Record<string, string>>;
    } catch {
      return null;
    }
  }, [page, cfg.page_about_json, cfg.page_fiscal_json, cfg.page_contact_json]);

  // Costruita fuori dal render (cmsReader) e memorizzata: un valore creato
  // durante il render non va modificato dopo, e così resta anche stabile.
  return useMemo(() => cmsReader(cmsData, lang, page, t), [cmsData, lang, page, t]);
}

/** Il lettore dei testi: `ct(chiave)` per il testo, `ct.image(chiave)` per le immagini. */
function cmsReader(
  cmsData: Record<string, Record<string, string>> | null,
  lang: string,
  page: string,
  t: (key: string) => string,
) {
  const ct = (key: string): string => {
    const cmsValue = cmsData?.[lang]?.[key];
    if (cmsValue && cmsValue.trim()) return cmsValue;
    return t(`${page}.${key}`);
  };
  ct.image = (key: string): string | null => {
    return cmsData?.["_images"]?.[key] || null;
  };
  return ct;
}
